// @safetyhub/report-composer — 레이아웃 변환 (순수 함수)
//
// NodeLayout(flow 기반, 절대좌표 없음)을 React CSSProperties로 변환한다.
// 크기(자동/채움/고정)와 정렬(위치, alignH/alignV)을 직교 분리한다 — "채움"은 오직 size='fill'.
// 부모 컨텍스트(group flex / stack grid / flow)에 따라 size='fill'을 올바른 CSS로 변환한다.
// 결정 기록: docs/decisions/2026-06-25-composer-unified-size-align-model.md

import type { CSSProperties } from 'react';
import type { NodeLayout, BoxSpacing, RectStyle, TableCellStyle } from '@safetyhub/shared';

/** 부모 렌더 컨텍스트 — size='fill'/정렬을 컨텍스트별로 변환하기 위함. undefined=flow(기본 column 흐름). */
export type ParentContext =
  | { kind: 'group'; direction: 'row' | 'column' }
  | { kind: 'stack' }
  | undefined;

/** BoxSpacing → CSS shorthand("t r b l"). 미지정 항목은 0. */
export function boxSpacingToCss(spacing: BoxSpacing): string {
  const t = spacing.top ?? 0;
  const r = spacing.right ?? 0;
  const b = spacing.bottom ?? 0;
  const l = spacing.left ?? 0;
  return `${t}px ${r}px ${b}px ${l}px`;
}

/** 정렬 값(start/center/end) → flex align/justify 값. */
export function alignToFlex(align: 'start' | 'center' | 'end'): 'flex-start' | 'center' | 'flex-end' {
  switch (align) {
    case 'start':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'end':
      return 'flex-end';
  }
}

/** 정렬 값(start/center/end) → CSS Grid place-self 값(start/center/end 그대로). */
export function alignToGrid(align: 'start' | 'center' | 'end'): 'start' | 'center' | 'end' {
  return align;
}

/** Inner 수직정렬(start/center/end) → CSS vertical-align(table 셀용). */
export function vAlignToTableCss(v: 'start' | 'center' | 'end'): 'top' | 'middle' | 'bottom' {
  return v === 'start' ? 'top' : v === 'end' ? 'bottom' : 'middle';
}

/** Inner 수평정렬(start/center/end) → CSS text-align. */
export function contentHToTextAlign(h: 'start' | 'center' | 'end'): 'left' | 'center' | 'right' {
  return h === 'start' ? 'left' : h === 'end' ? 'right' : 'center';
}

/**
 * 텍스트-런 공통 스타일 필드 — TextStyle·TableCellStyle이 구조적으로 만족한다.
 * textStyleToCss 입력 계약(폰트/색/자간/줄간격만; textIndent·배경·테두리 등 사이트 고유는 제외).
 */
export interface TextRunStyle {
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  fontFamily?: string;
  italic?: boolean;
  underline?: boolean;
  letterSpacing?: number;
  lineHeight?: number;
}

/**
 * 텍스트-런 스타일 → CSSProperties. TextPrim·표 셀·체크박스 라벨이 공유하는
 * 폰트/굵기/색/폰트패밀리/이탤릭/밑줄/자간/줄간격 변환을 일원화한다(4곳 사본 통합).
 * 각 호출부는 이 결과에 자기 고유 속성(레이아웃 정렬·박스 장식·셀 배경/테두리/패딩·textIndent 등)만 더한다.
 * 미지정 필드는 속성 자체를 넣지 않아 CSS 상속을 유지한다(회귀 0).
 */
export function textStyleToCss(s?: TextRunStyle): CSSProperties {
  const css: CSSProperties = {};
  if (!s) return css;
  if (s.fontSize !== undefined) css.fontSize = `${s.fontSize}px`;
  if (s.fontWeight !== undefined) css.fontWeight = s.fontWeight;
  if (s.color !== undefined) css.color = s.color;
  if (s.fontFamily !== undefined) css.fontFamily = s.fontFamily;
  if (s.italic) css.fontStyle = 'italic';
  if (s.underline) css.textDecoration = 'underline';
  // 자간: 저장값은 글자 크기 대비 %(HWP 관례). em은 요소 font-size 상대라 % 의미와 일치.
  if (s.letterSpacing !== undefined) css.letterSpacing = `${s.letterSpacing / 100}em`;
  if (s.lineHeight !== undefined) css.lineHeight = s.lineHeight;
  return css;
}

/** size 값이 '채움'인지(미지정도 fill 취급 — width 미지정 기본은 fill). */
function isFill(size: number | string | undefined, treatUndefinedAsFill: boolean): boolean {
  if (size === 'fill') return true;
  if (size === undefined) return treatUndefinedAsFill;
  return false;
}

/** size 값을 명시적 CSS 크기로(고정 px / auto / % 등). fill/undefined는 호출 측이 별도 처리. */
function sizeToCss(size: number | string | undefined): string | undefined {
  if (size === undefined || size === 'fill') return undefined;
  if (typeof size === 'number') return `${size}px`;
  if (size === 'auto') return 'auto';
  return size; // 백분율 등 문자열
}

/**
 * NodeLayout → CSSProperties. 부모 컨텍스트에 따라 size='fill'과 정렬(alignH/V)을 변환한다.
 *
 * - parent=group(flex item): 주축(direction)은 fill→flex:1 1 0(basis 0, 균등 분할), 아니면 flex:'0 0 auto'+크기.
 *   교차축은 fill→alignSelf:'stretch', 아니면 alignSelf=정렬 매핑+크기.
 * - parent=stack: 크기만(fill→'100%', 고정→px, auto→미설정). place-self는 stack 렌더가 부여.
 * - flow(미지정): width fill→'100%', height fill→'100%', 고정→px, auto→미설정.
 */
export function nodeLayoutToStyle(layout?: NodeLayout, parent?: ParentContext): CSSProperties {
  const style: CSSProperties = {};

  const width = layout?.width;
  const height = layout?.height;

  if (parent?.kind === 'group') {
    const isRow = parent.direction === 'row';
    // 주축/교차축 식별. 주축=row이면 가로(width), column이면 세로(height).
    const mainSize = isRow ? width : height;
    const crossSize = isRow ? height : width;
    const crossAlign = isRow
      ? layout?.alignV // row 교차축=세로
      : layout?.alignH; // column 교차축=가로
    // 대칭 규칙(결정: 2026-06-25-unified-size-align + 2026-06-26-cross-axis-fill-gated-by-size):
    //  - 주축: 미지정=콘텐츠(채움은 size='fill' 명시만). row 주축 width도 더는 자동 grow 아님.
    //  - 교차축: 미지정/'fill'=stretch(equal-height row) / 'auto'·고정=정렬로 위치(size로 게이트, 정렬 무관).
    const mainUndefIsFill = false;
    // 교차축 미지정=기본 채움(stretch). 정렬 무관 — size로 게이트(height/width='auto' 명시 시 정렬로 위치).
    const crossUndefIsFill = true;

    // ── 주축 ──
    if (isFill(mainSize, mainUndefIsFill)) {
      // 주축 채움 = flex:1 1 0 (CSS 표준) — 콘텐츠 무관 남는 공간 균등 분할.
      // min-width/height는 auto 유지(콘텐츠가 균등 share를 넘으면 그 노드만 min-content에서 멈춰 overflow 방지).
      style.flexGrow = 1;
      style.flexBasis = 0;
    } else {
      style.flexGrow = 0;
      style.flexShrink = 0;
      style.flexBasis = 'auto';
      const css = sizeToCss(mainSize);
      if (css !== undefined) {
        if (isRow) style.width = css;
        else style.height = css;
      }
    }

    // ── 교차축 ──
    if (isFill(crossSize, crossUndefIsFill)) {
      style.alignSelf = 'stretch'; // 교차축 채움(빈 요소 안전)
    } else {
      // 정렬 지정 시에만 alignSelf 부여. 미지정이면 부모 align-items(기본값) 상속.
      if (crossAlign !== undefined) style.alignSelf = alignToFlex(crossAlign);
      const css = sizeToCss(crossSize);
      if (css !== undefined) {
        if (isRow) style.height = css;
        else style.width = css;
      }
    }
  } else if (parent?.kind === 'stack') {
    // 크기만. place-self는 stack 렌더가 자식별로 부여한다.
    if (isFill(width, true)) style.width = '100%';
    else {
      const css = sizeToCss(width);
      if (css !== undefined) style.width = css;
    }
    if (isFill(height, false)) style.height = '100%';
    else {
      const css = sizeToCss(height);
      if (css !== undefined) style.height = css;
    }
  } else {
    // flow(미지정 부모) — 기존 동작 보존. width fill→100%, height fill→100%.
    if (isFill(width, true)) style.width = '100%';
    else {
      const css = sizeToCss(width);
      if (css !== undefined) style.width = css;
    }
    if (isFill(height, false)) style.height = '100%';
    else {
      const css = sizeToCss(height);
      if (css !== undefined) style.height = css;
    }
  }

  // minHeight는 height가 명시적(고정 숫자/채움 fill)이 아닐 때 = 자동(콘텐츠 기준)일 때만
  // 최소 높이로 적용한다. 고정/채움은 명시적 크기이므로 minHeight가 그 값을 막으면 안 됨.
  const heightExplicit = isFill(height, false) || typeof height === 'number';
  if (layout?.minHeight !== undefined && !heightExplicit) {
    style.minHeight = `${layout.minHeight}px`;
  }

  if (layout?.grow !== undefined) {
    style.flexGrow = layout.grow;
  }

  if (layout?.margin !== undefined) {
    style.margin = boxSpacingToCss(layout.margin);
  }

  if (layout?.padding !== undefined) {
    style.padding = boxSpacingToCss(layout.padding);
  }

  return style;
}

/** outer(슬롯) 스타일 — nodeLayoutToStyle에서 padding만 제외(크기/위치/마진/minHeight 유지). 컨테이너 2겹 모델용. */
export function outerBoxStyle(layout?: NodeLayout, parent?: ParentContext): CSSProperties {
  const style = nodeLayoutToStyle(layout, parent);
  delete style.padding; // 패딩은 inner로 이전 → flex 분배에 안 끼어듦(패딩 무관 균등)
  return style;
}

/** inner(콘텐츠) 스타일 — 슬롯을 100% 채우고 padding을 안쪽으로 먹는다. 컨테이너 2겹 모델용. */
export function innerBoxStyle(layout?: NodeLayout): CSSProperties {
  const style: CSSProperties = { width: '100%', height: '100%', boxSizing: 'border-box' };
  if (layout?.padding !== undefined) style.padding = boxSpacingToCss(layout.padding);
  return style;
}

/**
 * RectStyle(채움/테두리/모서리) → target CSSProperties에 적용.
 * 테두리 우선순위: borderOverrides > borderSides > border 단일.
 *  - borderOverrides가 하나라도 있으면: 각 변 = override 지정값, 미지정 변은 borderSides(false=없음) → base border.
 *    (셀 borderOverrides와 동일 규칙 — resolveCellBorder 재사용.)
 *  - borderOverrides가 없으면: 기존 border+borderSides 로직(미설정=4변 전부, 회귀 0).
 */
export function applyRectDecoration(target: CSSProperties, rs?: RectStyle): void {
  if (!rs) return;
  if (rs.fill !== undefined) target.backgroundColor = rs.fill;
  const ov = rs.borderOverrides;
  const hasOverride =
    ov != null &&
    (ov.top !== undefined ||
      ov.right !== undefined ||
      ov.bottom !== undefined ||
      ov.left !== undefined);
  if (hasOverride) {
    const s = rs.borderSides;
    const base = rs.border;
    // 미지정 변의 base: borderSides가 그 변을 false로 껐으면 없음(undefined), 아니면 base border.
    const sideBase = (flag?: boolean) => (flag === false ? undefined : base);
    Object.assign(
      target,
      resolveCellBorder(undefined, {
        top: ov.top ?? sideBase(s?.top),
        right: ov.right ?? sideBase(s?.right),
        bottom: ov.bottom ?? sideBase(s?.bottom),
        left: ov.left ?? sideBase(s?.left),
      }),
    );
  } else if (rs.border !== undefined) {
    const s = rs.borderSides;
    if (!s || (s.top !== false && s.right !== false && s.bottom !== false && s.left !== false)) {
      target.border = rs.border; // 4변 전부
    } else {
      if (s.top !== false) target.borderTop = rs.border;
      if (s.right !== false) target.borderRight = rs.border;
      if (s.bottom !== false) target.borderBottom = rs.border;
      if (s.left !== false) target.borderLeft = rs.border;
    }
  }
  if (rs.borderRadius !== undefined) target.borderRadius = `${rs.borderRadius}px`;
}

/** 셀 변별 테두리 override(상/하/좌/우). 미지정 변은 base 상속. */
export interface CellBorderOverrides {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

/**
 * 셀 테두리 합성 — base `border` + 변별 `overrides`.
 *
 * - override가 하나도 없으면 `border` 단일값으로(기존 동작, 회귀 0). base도 없으면 빈 객체.
 * - override가 하나라도 있으면 각 변 = `overrides[변] ?? border`로 분리 적용.
 *   해당 변 값이 undefined면(override·base 둘 다 없음) 그 변은 미설정(CSS 기본).
 *
 * 반환은 CSSProperties 부분(`border` 단일 또는 `borderTop/Right/Bottom/Left` 분리)이라
 * 호출처에서 `Object.assign(style, ...)`로 적용한다. applyRectDecoration의 borderSides와 동형.
 */
export function resolveCellBorder(
  border: string | undefined,
  overrides: CellBorderOverrides | undefined,
): Pick<CSSProperties, 'border' | 'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft'> {
  const hasOverride =
    overrides != null &&
    (overrides.top !== undefined ||
      overrides.right !== undefined ||
      overrides.bottom !== undefined ||
      overrides.left !== undefined);
  if (!hasOverride) {
    return border !== undefined ? { border } : {};
  }
  const out: Pick<
    CSSProperties,
    'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft'
  > = {};
  const top = overrides!.top ?? border;
  const right = overrides!.right ?? border;
  const bottom = overrides!.bottom ?? border;
  const left = overrides!.left ?? border;
  if (top !== undefined) out.borderTop = top;
  if (right !== undefined) out.borderRight = right;
  if (bottom !== undefined) out.borderBottom = bottom;
  if (left !== undefined) out.borderLeft = left;
  return out;
}

/**
 * TableCellStyle(정적 부분) → CSSProperties. 폰트(textStyleToCss) + 배경 + 테두리(변별 override
 * 합성) + 패딩을 한 곳에 일원화한다. 양식 탭 TableCell 렌더(renderTableCell)와 formTable 고정 셀
 * 렌더(composerWidgetTreeRender)가 공유해 중복 합성을 없앤다.
 *
 * backgroundKey/backgroundKeyAlpha(데이터 바인딩 배경)는 resolve된 scope에 의존해 TableCell 렌더
 * 고유이므로 여기서 제외 — 호출처가 이 결과 위에 별도 우선 적용한다(정적 background를 덮어씀).
 * 미지정 필드는 속성 자체를 넣지 않아 CSS 상속을 유지한다(회귀 0).
 */
export function cellStyleToCss(style?: TableCellStyle): CSSProperties {
  const css = textStyleToCss(style);
  if (!style) return css;
  if (style.background !== undefined) css.background = style.background;
  Object.assign(css, resolveCellBorder(style.border, style.borderOverrides));
  if (style.padding !== undefined) css.padding = boxSpacingToCss(style.padding);
  // 대각선(diagonal)이 지정된 셀만 절대배치 오버레이의 기준 박스가 된다 — 조건부라 그 외 셀의
  // 배치 맥락은 종전 그대로(회귀 0). 오버레이 노드는 tableCellDiagonal의 cellDiagonalOverlay가
  // td 자식으로 방출한다(두 렌더 사이트 공유: renderTableCell / composerWidgetTreeRender 고정 셀).
  if (style.diagonal !== undefined) css.position = 'relative';
  return css;
}
